import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatsCard } from '@/components/dashboard/StatsCard';
import { IssuesChart } from '@/components/dashboard/IssuesChart';
import { RegionsChart } from '@/components/dashboard/RegionsChart';
import { RecentIssues } from '@/components/dashboard/RecentIssues';
import { supabase } from '@/lib/supabase';
import type { ReportedIssue } from '@/types';

interface Stats {
  totalIssues: number;
  uniqueSchools: number;
  statusCounts: Record<string, number>;
  regionCounts: { name: string; count: number }[];
  monthlyIssues: { month: string; count: number }[];
  recentIssues: ReportedIssue[];
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({
    totalIssues: 0,
    uniqueSchools: 0,
    statusCounts: {},
    regionCounts: [],
    monthlyIssues: [],
    recentIssues: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data: issues, error: issuesError } = await supabase
        .from('sms_reported_issues')
        .select(`
          *,
          school:sms_schools(
            name,
            region:sms_regions(name)
          ),
          grade:sms_grades(name)
        `)
        .order('created_at', { ascending: false });

      if (issuesError) throw issuesError;

      const totalIssues = issues.length;
      const uniqueSchools = new Set(issues.map(issue => issue.school_id)).size;

      const statusCounts = issues.reduce((acc: Record<string, number>, issue) => {
        acc[issue.status] = (acc[issue.status] || 0) + 1;
        return acc;
      }, {});

      const regionCounts = Object.entries(
        issues.reduce((acc: Record<string, number>, issue) => {
          const regionName = issue.school?.region?.name;
          if (regionName) {
            acc[regionName] = (acc[regionName] || 0) + 1;
          }
          return acc;
        }, {})
      ).map(([name, count]) => ({ name, count }));

      const monthlyIssues = Object.entries(
        issues.reduce((acc: Record<string, number>, issue) => {
          const month = new Date(issue.created_at).toLocaleString('en-US', { month: 'short', year: 'numeric' });
          acc[month] = (acc[month] || 0) + 1;
          return acc;
        }, {})
      )
        .map(([month, count]) => ({ month, count }))
        .slice(-12)
        .reverse();

      const recentIssues = issues.slice(0, 5);

      setStats({
        totalIssues,
        uniqueSchools,
        statusCounts,
        regionCounts,
        monthlyIssues,
        recentIssues,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground animate-pulse">
            Loading dashboard data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <Button onClick={() => navigate('/')} className="bg-primary">
          <AlertTriangle className="h-4 w-4 mr-2" />
          Report an Issue
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard title="Total Issues" value={stats.totalIssues} />
        <StatsCard title="Schools Affected" value={stats.uniqueSchools} />
        <StatsCard
          title="Open Issues"
          value={(stats.statusCounts['new'] || 0) + (stats.statusCounts['in review'] || 0)}
        />
        <StatsCard title="Resolved Issues" value={stats.statusCounts['resolved'] || 0} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <IssuesChart data={stats.monthlyIssues} />
        <RegionsChart data={stats.regionCounts} total={stats.totalIssues} />
      </div>

      <RecentIssues issues={stats.recentIssues} />
    </div>
  );
}